import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState, useMemo } from 'react';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Eye, Download, FileText, Clock, Printer, Truck, CheckCircle2, X, Tag,
  Search, Trash2, XCircle, Sparkles, AlertCircle
} from 'lucide-react';
import OrderChatPanel from '../../components/OrderChatPanel';

const BACKEND = RUNTIME_BACKEND_URL;

// Statu meta: aktif (sayım+filtreye dahil) ve cancelled (sadece kendi tab'ında)
const FLOW = [
  { v: 'gonderildi', l: 'Gönderildi', c: 'bg-slate-100 text-slate-700 border-slate-300',
    btn: 'bg-slate-500 hover:bg-slate-600 text-white', icon: Clock },
  { v: 'isleme_alindi', l: 'İşleme Al', c: 'bg-blue-100 text-blue-700 border-blue-300',
    btn: 'bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-200', icon: Eye },
  { v: 'baskida', l: 'Baskıda', c: 'bg-purple-100 text-purple-700 border-purple-300',
    btn: 'bg-purple-600 hover:bg-purple-700 text-white shadow-md shadow-purple-200', icon: Printer },
  { v: 'kargoda', l: 'Kargoda', c: 'bg-amber-100 text-amber-700 border-amber-300',
    btn: 'bg-amber-600 hover:bg-amber-700 text-white shadow-md shadow-amber-200', icon: Truck },
  { v: 'teslim_edildi', l: 'Teslim Edildi', c: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    btn: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-200', icon: CheckCircle2 },
];

const CANCELLED = { v: 'iptal', l: 'İptal', c: 'bg-red-100 text-red-700 border-red-300',
  btn: 'bg-red-600 hover:bg-red-700 text-white', icon: XCircle };

const ALL_STATUSES = [...FLOW, CANCELLED];

const CARGO_OPTIONS = [
  { v: 'yurtici', l: 'Yurtiçi Kargo' },
  { v: 'aras', l: 'Aras Kargo' },
  { v: 'mng', l: 'MNG Kargo' },
  { v: 'ptt', l: 'PTT Kargo' },
  { v: 'surat', l: 'Sürat Kargo' },
  { v: 'ups', l: 'UPS' },
  { v: 'hepsijet', l: 'HepsiJET' },
  { v: 'trendyol', l: 'Trendyol Express' },
  { v: 'other', l: 'Diğer' },
];

export default function AlbumcuOrdersPage() {
  const { token, user } = useAuthStore();
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all'); // all | gonderildi | ... | iptal
  const [search, setSearch] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);

  // Ship dialog
  const [showShip, setShowShip] = useState(false);
  const [shipForm, setShipForm] = useState({ cargo_company: 'yurtici', tracking_no: '', note: '' });
  const [shipping, setShipping] = useState(false);

  // ZIP download progress
  const [zipProgress, setZipProgress] = useState(null); // null | {pct, loaded, total, speed, eta, phase}

  const formatBytes = (b) => {
    if (b > 1024 * 1024) return (b / 1024 / 1024).toFixed(1) + ' MB';
    if (b > 1024) return (b / 1024).toFixed(0) + ' KB';
    return b + ' B';
  };
  const formatEta = (sec) => {
    if (sec === null || sec === undefined) return '';
    if (sec < 60) return `~${sec}sn`;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `~${m}dk ${s}sn`;
  };

  const downloadZip = (order) => {
    setZipProgress({ pct: 0, loaded: 0, total: 0, speed: 0, eta: null, phase: 'preparing' });
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${BACKEND}/api/print-orders/${order.id}/download-zip`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.responseType = 'blob';
    const startedAt = Date.now();
    let firstByteAt = null;

    xhr.onprogress = (e) => {
      if (!firstByteAt) firstByteAt = Date.now();
      const total = e.lengthComputable ? e.total : 0;
      const loaded = e.loaded || 0;
      const pct = total > 0 ? Math.round((loaded / total) * 100) : 0;
      const elapsedSec = (Date.now() - firstByteAt) / 1000;
      let speed = 0, eta = null;
      if (elapsedSec > 0.3) {
        speed = loaded / elapsedSec;
        if (speed > 0 && total > 0) {
          eta = Math.max(0, Math.round((total - loaded) / speed));
        }
      }
      setZipProgress({ pct, loaded, total, speed, eta, phase: 'downloading' });
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const blob = xhr.response;
        const cd = xhr.getResponseHeader('content-disposition') || '';
        let fname = 'siparis.zip';
        const m = cd.match(/filename\*=UTF-8''([^;]+)/) || cd.match(/filename="([^"]+)"/);
        if (m) {
          try { fname = decodeURIComponent(m[1]); } catch { fname = m[1]; }
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = fname; a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          setZipProgress(null);
        }, 500);
        toast.success('ZIP indirildi: ' + fname);
      } else {
        setZipProgress(null);
        toast.error(`ZIP indirilemedi: HTTP ${xhr.status}`);
      }
    };
    xhr.onerror = () => { setZipProgress(null); toast.error('ZIP indirilemedi: Ağ hatası'); };
    xhr.onabort = () => { setZipProgress(null); toast.error('İndirme iptal edildi'); };

    // Sunucunun ZIP hazırlama süresi başlamadan önceki bekleme
    setTimeout(() => {
      if (zipProgress?.phase === 'preparing' && !firstByteAt) {
        // hala başlamadıysa preparing göster (zaten state'te)
      }
    }, 800);
    xhr.send();
  };

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/print-orders`, { headers });
      setOrders(await res.json());
    } catch { toast.error('Yüklenemedi'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (o, newStatus) => {
    try {
      await fetch(`${BACKEND}/api/print-orders/${o.id}/status`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, note })
      });
      toast.success('Durum güncellendi');
      setNote('');
      setSelected(null);
      load();
    } catch { toast.error('Güncellenemedi'); }
  };

  const deleteOrder = async (o) => {
    if (!window.confirm(`"${o.product_name}" siparişini kalıcı olarak silmek istediğinize emin misiniz? Dosyalar ve mesajlar da silinir.`)) return;
    try {
      const r = await fetch(`${BACKEND}/api/print-orders/${o.id}`, { method: 'DELETE', headers });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Hata');
      }
      toast.success('Sipariş silindi');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const openShipDialog = (o) => {
    setShipForm({
      cargo_company: o.cargo_company || 'yurtici',
      tracking_no: o.cargo_tracking_no || '',
      note: ''
    });
    setShowShip(true);
  };

  const submitShip = async () => {
    if (!shipForm.tracking_no.trim()) {
      toast.error('Kargo takip numarası zorunlu');
      return;
    }
    setShipping(true);
    try {
      const r = await fetch(`${BACKEND}/api/print-orders/${selected.id}/ship`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(shipForm)
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Hata');
      }
      toast.success('Sipariş kargoya verildi');
      setShowShip(false);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setShipping(false);
    }
  };

  const openCargoLabel = (o) => {
    fetch(`${BACKEND}/api/print-orders/${o.id}/cargo-label`, { headers })
      .then(r => { if (!r.ok) throw new Error('Etiket alınamadı'); return r.text(); })
      .then(html => {
        const w = window.open('', '_blank');
        if (!w) { toast.error("Pop-up engellenmiş — etiket için pop-up'a izin verin"); return; }
        w.document.write(html);
        w.document.close();
      })
      .catch(() => toast.error('Kargo etiketi açılamadı'));
  };

  // İptal hariç tüm siparişler (Tümü ve diğer aktif tabler için)
  const activeOrders = useMemo(() => orders.filter(o => o.status !== 'iptal'), [orders]);
  const cancelledOrders = useMemo(() => orders.filter(o => o.status === 'iptal'), [orders]);

  // Arama uygulama (içinde bulunan listeye göre)
  const applySearch = (list) => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(o => {
      const hay = [
        o.photographer_name, o.customer_name, o.product_name,
        o.bride_name, o.groom_name, o.cargo_tracking_no, o.cargo_company_name,
        o.wedding_date ? new Date(o.wedding_date).toLocaleDateString('tr-TR') : '',
        new Date(o.created_at).toLocaleDateString('tr-TR'),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  };

  // Filtrelenmiş + aranmış nihai liste
  const visible = useMemo(() => {
    if (filter === 'iptal') return applySearch(cancelledOrders);
    if (filter === 'all') return applySearch(activeOrders);
    return applySearch(activeOrders.filter(o => o.status === filter));
  }, [filter, search, activeOrders, cancelledOrders]);

  // Sayımlar — iptal hariç (Tümü iptal'ı dahil etmez)
  const counts = useMemo(() => {
    const map = { all: activeOrders.length, iptal: cancelledOrders.length };
    FLOW.forEach(f => { map[f.v] = activeOrders.filter(o => o.status === f.v).length; });
    return map;
  }, [activeOrders, cancelledOrders]);

  return (
    <div className="space-y-4" data-testid="albumcu-orders-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            Gelen Baskı Siparişleri
          </h1>
          <p className="text-sm text-slate-500">Fotoğrafçıların size gönderdiği albüm baskı siparişleri</p>
        </div>
      </div>

      {/* Arama */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Tarih, fotoğrafçı, müşteri, ürün, kargo takip no ile arayın..."
          className="pl-9 h-11"
          data-testid="orders-search-input"
        />
        {search && (
          <button onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Filter Tabs - daha dikkat çekici, renkli */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter('all')}
          data-testid="filter-all"
          className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all border-2 ${
            filter === 'all'
              ? 'bg-primary text-white border-primary shadow-lg shadow-primary/20 scale-105'
              : 'bg-white text-slate-700 border-slate-200 hover:border-primary/50'
          }`}>
          <span className="flex items-center gap-1.5">
            <Sparkles className="h-4 w-4" />
            Tümü
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${filter === 'all' ? 'bg-white/20' : 'bg-slate-100'}`}>
              {counts.all}
            </span>
          </span>
        </button>

        {FLOW.map(f => {
          const Ic = f.icon;
          const isActive = filter === f.v;
          return (
            <button key={f.v}
              onClick={() => setFilter(f.v)}
              data-testid={`filter-${f.v}`}
              className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all border-2 ${
                isActive
                  ? `${f.btn} border-transparent shadow-lg scale-105`
                  : 'bg-white text-slate-700 border-slate-200 hover:border-current'
              }`}>
              <span className="flex items-center gap-1.5">
                <Ic className="h-4 w-4" />
                {f.l}
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                  isActive ? 'bg-white/20' : counts[f.v] > 0 ? 'bg-amber-100 text-amber-800 font-bold' : 'bg-slate-100'
                }`}>
                  {counts[f.v] || 0}
                </span>
              </span>
            </button>
          );
        })}

        {/* İptal Edilenler - ayrı sekme */}
        <button
          onClick={() => setFilter('iptal')}
          data-testid="filter-iptal"
          className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all border-2 ml-auto ${
            filter === 'iptal'
              ? 'bg-red-600 text-white border-red-600 shadow-lg shadow-red-200 scale-105'
              : 'bg-white text-red-600 border-red-200 hover:border-red-400'
          }`}>
          <span className="flex items-center gap-1.5">
            <XCircle className="h-4 w-4" />
            İptal Edilenler
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${filter === 'iptal' ? 'bg-white/20' : 'bg-red-100'}`}>
              {counts.iptal}
            </span>
          </span>
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400">Yükleniyor...</div>
      ) : visible.length === 0 ? (
        <Card><CardContent className="p-10 text-center">
          <FileText className="h-12 w-12 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">
            {search
              ? `"${search}" için sonuç bulunamadı`
              : filter === 'iptal'
                ? 'İptal edilmiş sipariş yok'
                : 'Bu durumda sipariş yok'}
          </p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {visible.map(o => {
            const si = ALL_STATUSES.find(f => f.v === o.status) || FLOW[0];
            const SIcon = si.icon;
            const isCancelled = o.status === 'iptal';
            return (
              <Card key={o.id} data-testid={`order-${o.id}`}
                className={`hover:shadow-md transition ${isCancelled ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-primary">{o.photographer_name}</span>
                        <span className="text-slate-400">→</span>
                        <span className="font-medium">{o.product_name}</span>
                        <span className="text-emerald-600 font-bold">{o.product_price?.toLocaleString('tr-TR')} TL</span>
                      </div>
                      {(o.variants || []).length > 0 && (
                        <div className="text-xs text-slate-500 mt-1">
                          <b>Ebat / Seçenekler:</b> {(o.variants || []).map(v => v.name).join(' · ')}
                        </div>
                      )}
                      <div className="text-xs text-slate-600 mt-1">
                        Albüm İsimleri: <b>{o.customer_name || '-'}</b>
                        {o.wedding_date && <span> · Tarih: {new Date(o.wedding_date).toLocaleDateString('tr-TR')}</span>}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {new Date(o.created_at).toLocaleString('tr-TR')} · {(o.files || []).length} dosya · Tel: {o.photographer_phone || '-'}
                      </div>
                      {(o.cargo_company_name || o.cargo_tracking_no) && (
                        <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 mt-2 inline-flex items-center gap-2">
                          <Truck className="h-3.5 w-3.5 text-amber-600" />
                          <b>{o.cargo_company_name}</b>
                          {o.cargo_tracking_no && <span className="font-mono">· {o.cargo_tracking_no}</span>}
                        </div>
                      )}
                      {o.notes && (
                        <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                          <b>Not:</b> {o.notes}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs font-medium ${si.c}`}>
                        <SIcon className="h-3 w-3" />{si.l}
                      </span>
                      <Button size="sm" onClick={() => { setSelected(o); setNote(''); }}>
                        <Eye className="h-3.5 w-3.5 mr-1" />Aç
                      </Button>
                      {isCancelled && (
                        <Button size="sm" variant="outline"
                          onClick={() => deleteOrder(o)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          data-testid={`delete-cancelled-${o.id}`}>
                          <Trash2 className="h-3.5 w-3.5 mr-1" />Sil
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail + Status Update Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Sipariş Yönetimi</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="bg-primary/5 rounded-lg p-3 text-sm space-y-1">
                <div><b>Fotoğrafçı:</b> {selected.photographer_name} · {selected.photographer_phone || '-'}</div>
                <div>
                  <b>Ürün:</b> {selected.product_name} - {selected.product_price?.toLocaleString('tr-TR')} TL
                </div>
                {(selected.variants || []).length > 0 && (
                  <div className="ml-4 text-xs text-slate-600">
                    <b>Ebat / Seçenekler:</b>{' '}
                    {(selected.variants || []).map(v => `${v.name}${v.price ? ` (+${v.price.toLocaleString('tr-TR')} TL)` : ''}`).join(' · ')}
                  </div>
                )}
                <div><b>Albüm İsimleri:</b> {selected.customer_name}</div>
                {selected.wedding_date && <div><b>Tarih:</b> {new Date(selected.wedding_date).toLocaleDateString('tr-TR')}</div>}
                {(selected.photographer_address || selected.photographer_city) && (
                  <div className="pt-1 border-t mt-1">
                    <b>Kargo Adresi:</b>{' '}
                    {selected.photographer_address}
                    {(selected.photographer_district || selected.photographer_city) && (
                      <span> ({[selected.photographer_district, selected.photographer_city].filter(Boolean).join('/')})</span>
                    )}
                    {selected.photographer_postal_code && <span> · {selected.photographer_postal_code}</span>}
                  </div>
                )}
                {selected.notes && <div className="pt-1"><b>Not:</b> {selected.notes}</div>}
                {(selected.cargo_company_name || selected.cargo_tracking_no) && (
                  <div className="pt-1 border-t mt-1 flex items-center gap-2 text-amber-700">
                    <Truck className="h-3.5 w-3.5" />
                    <b>{selected.cargo_company_name}</b>
                    {selected.cargo_tracking_no && <span className="font-mono">· {selected.cargo_tracking_no}</span>}
                  </div>
                )}
              </div>

              <div>
                <p className="text-xs font-bold mb-2 flex items-center justify-between">
                  <span>Dosyalar ({(selected.files || []).length})</span>
                  {(selected.files || []).length > 0 && !zipProgress && (
                    <button onClick={() => downloadZip(selected)}
                      className="text-xs flex items-center gap-1 text-primary font-semibold hover:underline"
                      data-testid="download-zip-btn">
                      <Download className="h-3.5 w-3.5" />Tümünü ZIP Olarak İndir
                    </button>
                  )}
                </p>

                {/* ZIP download progress */}
                {zipProgress && (
                  <div className="mb-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="zip-progress-panel">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-bold text-emerald-700 flex items-center gap-1">
                        <Download className="h-3.5 w-3.5 animate-bounce" />
                        {zipProgress.phase === 'preparing'
                          ? 'Sunucu ZIP hazırlıyor...'
                          : `İndiriliyor... ${zipProgress.pct}%`}
                      </span>
                      <span className="text-slate-600">
                        {zipProgress.total > 0 && (
                          <>
                            {formatBytes(zipProgress.loaded)} / {formatBytes(zipProgress.total)}
                            {zipProgress.speed > 0 && <span className="ml-1.5">• {formatBytes(zipProgress.speed)}/sn</span>}
                            {zipProgress.eta !== null && <span className="ml-1.5">• {formatEta(zipProgress.eta)}</span>}
                          </>
                        )}
                        {zipProgress.total === 0 && zipProgress.loaded > 0 && (
                          <>{formatBytes(zipProgress.loaded)} indirildi</>
                        )}
                      </span>
                    </div>
                    <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden" data-testid="zip-progress-bar">
                      <div
                        className={`h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-200 relative overflow-hidden ${zipProgress.phase === 'preparing' ? 'animate-pulse w-1/4' : ''}`}
                        style={zipProgress.phase === 'preparing' ? {} : { width: `${zipProgress.pct}%` }}>
                        <div className="absolute inset-0 bg-white/20 animate-pulse" />
                      </div>
                    </div>
                    {zipProgress.pct >= 100 && (
                      <p className="text-[10px] text-emerald-700 mt-1">İndirme tamamlandı, dosya açılıyor...</p>
                    )}
                  </div>
                )}
                <div className="space-y-1">
                  {(selected.files || []).map((f, i) => (
                    <button key={i} onClick={async () => {
                      try {
                        const r = await fetch(`${BACKEND}/api/print-orders/${selected.id}/file/${f.stored}`, { headers });
                        const blob = await r.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = f.original_name; a.click();
                        URL.revokeObjectURL(url);
                      } catch { toast.error('İndirilemedi'); }
                    }}
                      className="w-full flex items-center justify-between bg-white border rounded p-2 text-xs hover:bg-slate-50">
                      <span className="flex items-center gap-2"><FileText className="h-3.5 w-3.5" />{f.original_name}</span>
                      <span className="flex items-center gap-1 text-primary">
                        <Download className="h-3.5 w-3.5" />İndir
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Kargo aksiyonları */}
              {(selected.status === 'baskida' || selected.status === 'isleme_alindi') && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between gap-2">
                  <div className="text-xs text-amber-800">
                    <b>Baskı tamamlandığında</b> kargoya verin — fotoğrafçıya bildirim ve takip no gider.
                  </div>
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700"
                    onClick={() => openShipDialog(selected)}
                    data-testid="open-ship-dialog-btn">
                    <Truck className="h-4 w-4 mr-1" />Kargoya Ver
                  </Button>
                </div>
              )}

              {selected.status === 'kargoda' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between gap-2">
                  <div className="text-xs text-emerald-800">
                    Kargoya verildi — kargo etiketini yazdırıp paketin üzerine yapıştırın.
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openShipDialog(selected)}>
                      Düzenle
                    </Button>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => openCargoLabel(selected)}
                      data-testid="print-cargo-label-btn">
                      <Tag className="h-4 w-4 mr-1" />Kargo Etiketi
                    </Button>
                  </div>
                </div>
              )}

              {/* İptal edilmiş sipariş uyarısı */}
              {selected.status === 'iptal' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between gap-2">
                  <div className="text-xs text-red-800 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Bu sipariş <b>iptal edilmiş</b>. Silebilir veya geçmişte tutabilirsiniz.
                  </div>
                  <Button size="sm" variant="outline"
                    onClick={() => deleteOrder(selected)}
                    className="text-red-600 border-red-300 hover:bg-red-100"
                    data-testid="delete-cancelled-btn">
                    <Trash2 className="h-4 w-4 mr-1" />Kalıcı Olarak Sil
                  </Button>
                </div>
              )}

              {/* Durum değiştirme — iptal edilmiş ise gizli */}
              {selected.status !== 'iptal' && (
                <div>
                  <p className="text-xs font-bold mb-1">Yeni Durum + Not</p>
                  <Textarea value={note} onChange={e => setNote(e.target.value)}
                    placeholder="Opsiyonel not..." rows={2} />
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {FLOW.filter(f => f.v !== selected.status && f.v !== 'kargoda').map(f => {
                      const Ic = f.icon;
                      return (
                        <Button key={f.v} size="sm"
                          className={`${f.btn} font-semibold`}
                          onClick={() => updateStatus(selected, f.v)}
                          data-testid={`set-status-${f.v}`}>
                          <Ic className="h-3.5 w-3.5 mr-1" />{f.l}
                        </Button>
                      );
                    })}
                    <Button size="sm" variant="outline" className="text-red-600 border-2 border-red-300 hover:bg-red-50 font-semibold"
                      onClick={() => updateStatus(selected, 'iptal')}
                      data-testid="set-status-iptal">
                      <XCircle className="h-3.5 w-3.5 mr-1" />İptal Et
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">"Kargoda" durumuna geçmek için yukarıdaki <b>Kargoya Ver</b> butonunu kullanın.</p>
                </div>
              )}

              <div>
                <p className="text-xs font-bold mb-1">Durum Geçmişi</p>
                <div className="space-y-1">
                  {(selected.status_history || []).map((h, i) => {
                    const sm = ALL_STATUSES.find(f => f.v === h.status);
                    return (
                      <div key={i} className="flex items-center gap-2 text-xs bg-white border rounded p-2">
                        <span className="font-medium">{sm?.l || h.status}</span>
                        <span className="text-slate-400">{new Date(h.at).toLocaleString('tr-TR')}</span>
                        {h.note && <span className="text-slate-500">· {h.note}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              <OrderChatPanel orderId={selected.id} token={token} currentUserId={user?.id} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Kargoya Ver Dialog */}
      <Dialog open={showShip} onOpenChange={setShowShip}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-amber-600" />
              Kargoya Ver
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Kargo Firması *</Label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                value={shipForm.cargo_company}
                onChange={(e) => setShipForm({ ...shipForm, cargo_company: e.target.value })}
                data-testid="ship-cargo-company-select">
                {CARGO_OPTIONS.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </div>
            <div>
              <Label>Takip Numarası *</Label>
              <Input
                value={shipForm.tracking_no}
                onChange={(e) => setShipForm({ ...shipForm, tracking_no: e.target.value })}
                placeholder="Örn: 1234567890"
                data-testid="ship-tracking-input"
              />
            </div>
            <div>
              <Label>Not (opsiyonel)</Label>
              <Textarea
                value={shipForm.note}
                onChange={(e) => setShipForm({ ...shipForm, note: e.target.value })}
                placeholder="Kargoya verirken eklemek istediğiniz not..."
                rows={2}
              />
            </div>
            {selected && (selected.photographer_address || selected.photographer_city) && (
              <div className="bg-slate-50 border rounded p-2 text-xs">
                <p className="font-bold text-slate-700 mb-1">Alıcı Adresi (otomatik)</p>
                <p className="text-slate-600">
                  <b>{selected.photographer_name}</b> · {selected.photographer_phone || '-'}<br />
                  {selected.photographer_address}
                  {(selected.photographer_district || selected.photographer_city) && (
                    <span> ({[selected.photographer_district, selected.photographer_city].filter(Boolean).join('/')})</span>
                  )}
                </p>
              </div>
            )}
            {selected && !selected.photographer_address && (
              <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700">
                ⚠ Fotoğrafçının kayıtlı kargo adresi yok. Lütfen mesajlaşma üzerinden adres bilgisini isteyin.
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setShowShip(false)}>İptal</Button>
              <Button onClick={submitShip} disabled={shipping || !shipForm.tracking_no.trim()}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="submit-ship-btn">
                <Truck className="h-4 w-4 mr-1" />
                {shipping ? 'Gönderiliyor...' : 'Kargoya Ver'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
